import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Compass,
  CheckCircle2,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { CLINIC, whatsappLink } from "../lib/clinic";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        preferred_contact: "email",
        primary_symptom: "",
        source: "contact_form",
      });
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error(
        "Something went wrong. Please WhatsApp us — we'll respond fast.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-light py-24 lg:py-32 border-t border-rb-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Conversion-focused final CTA */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="font-overline mb-4">Start with a quick message</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-rb-text leading-[1.05]">
            Ready to be{" "}
            <span className="italic text-[#2563EB]">clenchless</span>?
          </h2>
          <p className="mt-6 text-rb-text-2 text-base lg:text-lg leading-relaxed">
            WhatsApp the clinic. Dr. Patel and the team usually reply within
            the hour — no forms, no pressure, just honest answers.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cta-whatsapp"
              className="btn-primary inline-flex items-center justify-center gap-2 px-10"
            >
              <MessageCircle size={18} strokeWidth={1.75} />
              Enquire on WhatsApp
            </a>
            <a
              href={CLINIC.phoneRaw}
              data-testid="contact-cta-call"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-10"
            >
              <Phone size={18} strokeWidth={1.75} />
              Call {CLINIC.phoneDisplay}
            </a>
          </div>

          <div className="mt-3">
            <button
              onClick={() => setOpen(!open)}
              data-testid="contact-toggle-form"
              className="text-sm text-rb-text-2 hover:text-rb-text inline-flex items-center gap-1 mt-3"
            >
              Prefer email? Send us a quick message
              <ChevronDown
                size={14}
                className={`transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="bg-white rounded-2xl border border-rb-border p-6 sm:p-8 mt-6 text-left">
                {!submitted ? (
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="contact-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input
                        required
                        value={form.name}
                        onChange={handleChange("name")}
                        data-testid="contact-name-input"
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-xl border border-rb-border bg-white focus:outline-none focus:border-[#2563EB] text-rb-text text-sm"
                      />
                      <input
                        required
                        type="tel"
                        value={form.phone}
                        onChange={handleChange("phone")}
                        data-testid="contact-phone-input"
                        placeholder="Phone"
                        className="w-full px-4 py-3 rounded-xl border border-rb-border bg-white focus:outline-none focus:border-[#2563EB] text-rb-text text-sm"
                      />
                    </div>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                      data-testid="contact-email-input"
                      placeholder="Email"
                      className="w-full px-4 py-3 rounded-xl border border-rb-border bg-white focus:outline-none focus:border-[#2563EB] text-rb-text text-sm"
                    />
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={handleChange("message")}
                      data-testid="contact-message-input"
                      placeholder="Anything you'd like us to know? (optional)"
                      className="w-full px-4 py-3 rounded-xl border border-rb-border bg-white focus:outline-none focus:border-[#2563EB] text-rb-text text-sm"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                      className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending...
                        </>
                      ) : (
                        <>Send message</>
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8" data-testid="contact-success-state">
                    <div className="inline-flex h-12 w-12 rounded-full bg-[#F1F5F9] items-center justify-center mb-4">
                      <CheckCircle2 size={24} strokeWidth={1.5} className="text-[#2563EB]" />
                    </div>
                    <h3 className="font-serif-display text-2xl text-rb-text mb-1">
                      Thank you, {form.name.split(" ")[0]}.
                    </h3>
                    <p className="text-rb-text-2 text-sm">
                      We'll be in touch shortly.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Clinic info — single row, scannable */}
        <div className="grid md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-soft p-6"
            data-testid="clinic-info-visit"
          >
            <div className="font-overline mb-2">Visit</div>
            <div className="font-serif-display text-xl text-rb-text leading-snug mb-1">
              {CLINIC.address.line1}
            </div>
            <div className="text-sm text-rb-text-2 mb-4">
              {CLINIC.address.line2}, {CLINIC.address.postcode}
              <br />
              {CLINIC.address.city}
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={CLINIC.mapDirectionsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="directions-link"
                className="text-xs px-3 py-1.5 rounded-full border border-rb-border hover:border-[#2563EB] inline-flex items-center gap-1"
              >
                <MapPin size={12} /> Directions
              </a>
              <a
                href={CLINIC.what3wordsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="w3w-link"
                className="text-xs px-3 py-1.5 rounded-full border border-rb-border hover:border-[#2563EB] inline-flex items-center gap-1"
              >
                <Compass size={12} /> ///{CLINIC.what3words}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="card-soft p-6"
            data-testid="clinic-info-contact"
          >
            <div className="font-overline mb-2">Direct lines</div>
            <a
              href={CLINIC.phoneRaw}
              data-testid="contact-phone-link"
              className="flex items-center gap-2.5 py-2.5 border-b border-rb-border text-sm text-rb-text hover:text-[#2563EB]"
            >
              <Phone size={14} strokeWidth={1.5} />
              <span>{CLINIC.phoneDisplay}</span>
            </a>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-center gap-2.5 py-2.5 border-b border-rb-border text-sm text-rb-text hover:text-[#25D366]"
            >
              <MessageCircle size={14} strokeWidth={1.5} />
              <span>Chat on WhatsApp</span>
            </a>
            <a
              href={`mailto:${CLINIC.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-2.5 py-2.5 text-sm text-rb-text hover:text-[#2563EB]"
            >
              <Mail size={14} strokeWidth={1.5} />
              <span>{CLINIC.email}</span>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="card-soft p-6"
            data-testid="clinic-info-hours"
          >
            <div className="font-overline mb-3">Hours</div>
            {CLINIC.hours.map((h) => (
              <div
                key={h.day}
                className="flex justify-between text-sm py-2 border-b last:border-b-0 border-rb-border"
              >
                <span className="text-rb-text-2">{h.day}</span>
                <span className="text-rb-text">{h.time}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
